/**
 * Validates a single value against one rule.
 * @param {*} value
 * @param {{ required?: boolean, type?: string, max?: number, pattern?: RegExp, transform?: Function, message: string }} rule
 * @returns {string|null} Error message or null if valid
 */
function validateRule(value, rule) {
    const checkValue = rule.transform ? rule.transform(value) : value;

    if (rule.required && !checkValue) {
        return rule.message;
    }

    if (checkValue === null || checkValue === undefined || checkValue === '') {
        return null;
    }

    if (rule.type === 'number' && typeof checkValue !== 'number') {
        return rule.message;
    }

    if (rule.max !== undefined && typeof checkValue === 'string' && checkValue.length > rule.max) {
        return rule.message;
    }

    if (rule.pattern && !rule.pattern.test(String(checkValue))) {
        return rule.message;
    }

    return null;
}

/**
 * Validates form data against a rules object.
 * @param {Record<string, *>} formData
 * @param {Record<string, Array<object>>} rules
 * @returns {{ errors: Record<string, string>, firstErrorField: string|null }}
 */
export const validateFormWithRules = (formData, rules) => {
    const errors = {};
    let firstErrorField = null;

    for (const [field, fieldRules] of Object.entries(rules)) {
        const value = formData[field];

        for (const rule of fieldRules) {
            const error = validateRule(value, rule);

            if (error) {
                errors[field] = error;
                firstErrorField ??= field;
                break;
            }
        }
    }

    return { errors, firstErrorField };
};

export const createFormRules = ($t) => {
    const requiredMessage = $t.__('common.labels.this_field_is_required');

    return {
        supplier: [
            {
                required: true,
                message: requiredMessage,
            },
        ],
        documentDate: [{ required: true, message: requiredMessage }],
        dueDate: [{ required: true, message: requiredMessage }],
        currency: [{ required: true, message: requiredMessage }],
        type: [{ required: true, message: requiredMessage }],
        step: [{ required: true, message: requiredMessage }],
        externalDocumentName: [
            {
                type: 'string',
                max: 100,
                message: $t.__('ocr.OcrVerificationForm.validation.document_number_max_length'),
            },
        ],
    };
};

export const createProductLineRules = ($t) => {
    const requiredMessage = $t.__('common.labels.this_field_is_required');

    return {
        description: [
            {
                required: true,
                message: requiredMessage,
            },
        ],
        accountingCode: [
            {
                required: true,
                message: requiredMessage,
            },
        ],
        taxRateId: [
            {
                required: true,
                message: requiredMessage,
            },
            {
                type: 'number',
                message: requiredMessage,
            },
        ],
        priceWithoutTax: [
            { required: true, message: requiredMessage },
            {
                type: 'string',
                pattern: /^-?\d*[.,]?\d*$/,
                message: requiredMessage,
                transform: (value) => (value !== null ? value.toString() : null),
            },
        ],
    };
};

export const isPaymentFieldRequired = (payment) => {
    const hasDate = !_.isEmpty(payment.date);
    const hasReference = !_.isEmpty(payment.reference);
    const hasAmount = !_.isNil(payment.amount) && payment.amount !== '';
    const hasPaymentMean = _.isNumber(payment.payment_mean);

    return hasDate || hasReference || hasAmount || hasPaymentMean;
};
